import React, { useState, useEffect } from "react";
import { Compo, CompoJSON } from "../models/compos";
import { RouteComponentProps, useHistory } from "react-router";
import { api } from "../api";
import { SyncListenJSON } from "../../../src/apidefs";
import { useUser } from "../state";
import Selector from "../DesignSystem/Selector";
import {
  CompoCategoriesClient,
  CompoCategoriesJSON,
} from "../../../src/entity/CompoCategoriesClient";
import { DateTimeInput } from "../DesignSystem/DateTimeInput";
import { TestIds } from "../testIds";
import { ConfirmModal } from "../DesignSystem/ConfirmModal";
import {
  ClipboardDocumentListIcon,
  CheckCircleIcon,
  TrophyIcon,
  CurrencyDollarIcon,
  SparklesIcon,
  Cog6ToothIcon,
  TagIcon,
  GiftIcon,
  CheckIcon,
  XMarkIcon,
  MusicalNoteIcon,
} from "@heroicons/react/24/outline";
import { Button } from "../DesignSystem/Button";

const Card = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div
    className={`rounded-2xl border border-slate-200 bg-white p-6 dark:border-white/10 dark:bg-slate-800 ${className}`}
  >
    {children}
  </div>
);

const CardTitle = ({
  children,
  icon,
}: {
  children: React.ReactNode;
  icon?: React.ReactNode;
}) => (
  <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold text-slate-800 dark:text-white">
    {icon && <span className="text-slate-500 dark:text-slate-400">{icon}</span>}
    {children}
  </h3>
);

const FormField = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className="space-y-1.5">
    <label className="block text-sm font-medium text-slate-600 dark:text-slate-300">
      {label}
    </label>
    {children}
  </div>
);

const inputClassName =
  "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-700 dark:text-white";
const textareaClassName = `${inputClassName} resize-y min-h-[80px]`;
const checkboxClassName =
  "rounded border-slate-300 bg-white text-blue-500 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-700 dark:focus:ring-offset-slate-800";

export function CompoAdminView(
  props: RouteComponentProps<{ id: string }>
): JSX.Element {
  const user = useUser();
  const history = useHistory();
  const [compo, setCompo] = useState<Compo | undefined>(undefined);
  const [categories, setCategories] = useState<
    CompoCategoriesJSON[] | undefined
  >(undefined);
  const [votesPerEntry, setVotesPerEntry] = useState<number | undefined>(
    undefined
  );
  const [statusMessage, setStatusMessage] = useState<string | undefined>(
    undefined
  );
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    variant?: "danger" | "primary";
  }>({ isOpen: false, title: "", message: "", onConfirm: () => {} });
  const [votesAlreadyExistModal, setVotesAlreadyExistModal] = useState<{
    isOpen: boolean;
    uniqueVoters: number;
    totalVotes: number;
  }>({ isOpen: false, uniqueVoters: 0, totalVotes: 0 });
  const [finalizeModalOpen, setFinalizeModalOpen] = useState(false);
  const [finalizeConfirmed, setFinalizeConfirmed] = useState(false);
  const [commitStatus, setCommitStatus] = useState<
    "idle" | "loading" | "completed"
  >("idle");
  const [syncListen, setSyncListen] = useState<SyncListenJSON | null | undefined>(undefined);
  const [syncListenLoading, setSyncListenLoading] = useState(false);

  const compo_id = Number(props.match.params.id);

  useEffect(() => {
    const fetchCompo = async () => {
      const result = await api.get<"/api/compo/:id">(`/api/compo/${compo_id}`);

      if (api.isToast(result)) {
        alert("something bad happened, tell johnfn quick!!!");
        return;
      }

      setVotesPerEntry(result.votesPerEntry);
      setCompo(new Compo(result.compo));
      setCategories(result.categories);
    };

    fetchCompo();
  }, [compo_id]);

  useEffect(() => {
    const fetchSyncListen = async () => {
      const result = await api.get<"/api/compo/:id/sync-listen">(
        `/api/compo/${compo_id}/sync-listen`
      );

      if (api.isToast(result)) {
        return;
      }

      setSyncListen(result.syncListen);
    };

    fetchSyncListen();
  }, [compo_id]);

  const handleGenerateVotes = async (compoId: number) => {
    const response = await api.post<"/api/compo/:id/admin/gen_votes_required">(
      `/api/compo/${compoId}/admin/gen_votes_required`
    );

    if (api.isToast(response)) {
      return;
    }

    setStatusMessage("Required votes generated successfully.");
  };

  const handleSyncListenClick = async () => {
    setSyncListenLoading(true);

    // Create sync listen if it doesn't exist
    const response = await api.post<"/api/compo/:id/sync-listen">(
      `/api/compo/${compo_id}/sync-listen`
    );

    setSyncListenLoading(false);

    if (api.isToast(response)) {
      return;
    }

    // Navigate to the edit page
    history.push(`/compo/${compo_id}/sync/edit`);
  };

  const handleGenerateVotesClick = async (compoId: number) => {
    const statusResponse =
      await api.get<"/api/compo/:id/admin/gen_votes_required/status">(
        `/api/compo/${compoId}/admin/gen_votes_required/status`
      );

    if (api.isToast(statusResponse)) {
      return;
    }

    if (statusResponse.hasExistingVotes) {
      setVotesAlreadyExistModal({
        isOpen: true,
        uniqueVoters: statusResponse.uniqueVoters,
        totalVotes: statusResponse.totalVotes,
      });
    } else {
      handleGenerateVotes(compoId);
    }
  };

  const handleCommitVotingResults = async () => {
    setCommitStatus("loading");
    const response = await api.get<"/api/compo/:id/commit">(
      `/api/compo/${compo_id}/commit`
    );

    if (api.isToast(response)) {
      setCommitStatus("idle");
      return;
    }

    setCommitStatus("completed");
  };

  const updateCompo = (flags: Partial<CompoJSON>) => {
    setCompo((prev) => {
      if (!prev) {
        return prev;
      }

      return new Compo({
        ...(prev.toJSON() as CompoJSON),
        ...flags,
      });
    });
  };

  const showConfirm = (
    title: string,
    message: string,
    onConfirm: () => void,
    variant: "danger" | "primary" = "danger"
  ) => {
    setConfirmModal({ isOpen: true, title, message, onConfirm, variant });
  };

  if (user.type !== "logged-in" || !user.user.admin || !compo) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100 dark:bg-slate-900">
        <div className="text-slate-500 dark:text-slate-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 px-4 pb-8 pt-16 dark:bg-slate-900">
      <div className="mx-auto max-w-4xl space-y-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-800 dark:text-white">
            {compo.name}
          </h1>
          <p className="mt-1 text-slate-500 dark:text-slate-400">
            Panel of Important Stuff !!!
          </p>
        </div>

        {statusMessage && (
          <div
            className="rounded-lg border border-emerald-500/30 bg-emerald-100 px-4 py-3 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400"
            data-test="generate-votes-success"
            data-testid={TestIds.AdminGenerateVotesSuccess}
          >
            {statusMessage}
          </div>
        )}

        {/* Compo Actions */}
        <Card>
          <CardTitle icon={<ClipboardDocumentListIcon className="h-5 w-5" />}>
            Compo Actions
          </CardTitle>

          <div className="space-y-4">
            <div>
              <p className="mb-2 text-sm font-medium text-slate-500 dark:text-slate-400">
                Before judging:
              </p>
              <Button
                onClick={() => {
                  handleGenerateVotesClick(compo_id);
                }}
                data-testId={TestIds.AdminGenerateVotesLink}
                icon={<SparklesIcon className="h-4 w-4" />}
                grow={false}
              >
                Generate required votes
              </Button>
            </div>

            <div>
              <p className="mb-2 text-sm font-medium text-slate-500 dark:text-slate-400">
                Before sync listen:
              </p>
              <Button
                onClick={handleSyncListenClick}
                isLoading={syncListenLoading}
                icon={<MusicalNoteIcon className="h-4 w-4" />}
                grow={false}
              >
                {syncListen ? "Edit sync listen" : "Create sync listen"}
              </Button>
            </div>

            <div>
              <p className="mb-2 text-sm font-medium text-slate-500 dark:text-slate-400">
                Before releasing scores:
              </p>
              <div className="flex flex-col items-start gap-2">
                <div className="flex items-center gap-4">
                  <Button
                    disabled={commitStatus === "completed" || compo.released}
                    variant="danger"
                    isLoading={commitStatus === "loading"}
                    icon={<CheckCircleIcon className="h-4 w-4" />}
                    onClick={() => {
                      if (commitStatus === "idle") {
                        const allChecksPassed =
                          (votesPerEntry || 0) >= 8 &&
                          new Date() > compo.judgingStopDate;
                        setFinalizeConfirmed(allChecksPassed);
                        setFinalizeModalOpen(true);
                      }
                    }}
                    grow={false}
                  >
                    {commitStatus === "completed" || compo.released
                      ? "Released!"
                      : "Release results"}
                  </Button>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Average votes per entry:{" "}
                    <strong className="text-slate-800 dark:text-white">
                      {votesPerEntry || 0}
                    </strong>
                  </p>
                </div>
                <Button
                  disabled={compo.sent_xp}
                  variant="success"
                  icon={<SparklesIcon className="h-4 w-4" />}
                  onClick={() => {
                    showConfirm(
                      "Send XP",
                      "Are you sure you want to send XP? The code will stop you from doing this more than once.",
                      async () => {
                        const divider = Number(
                          prompt(
                            "What number to divide XP by?\n\n Rule of thumb:\n Week compos: 1.\n Day compos: 2.\n Hour compos: 3."
                          )
                        );

                        if (isNaN(divider) || divider <= 0 || divider > 5) {
                          alert("Invalid divider.");
                          return;
                        }

                        await api.get<"/api/compo/:id/finalize-xp/:divider">(
                          `/api/compo/${compo_id}/finalize-xp/${divider}`
                        );

                        updateCompo({ sent_xp: true });
                        alert("Done!");
                      },
                      "primary"
                    );
                  }}
                  grow={false}
                >
                  Send XP
                </Button>
                <Button
                  disabled={compo.sent_trophies}
                  variant="primary"
                  icon={<TrophyIcon className="h-4 w-4" />}
                  onClick={() => {
                    showConfirm(
                      "Send Trophies",
                      "Are you sure you want to send trophies? The code will stop you from doing this more than once.",
                      async () => {
                        await api.get<"/api/compo/:id/admin/blast-trophies">(
                          `/api/compo/${compo_id}/admin/blast-trophies`
                        );

                        updateCompo({ sent_trophies: true });
                        alert("Done!");
                      },
                      "primary"
                    );
                  }}
                  grow={false}
                >
                  Send Trophies
                </Button>
                <Button
                  disabled={compo.sent_gold}
                  variant="admin"
                  icon={<CurrencyDollarIcon className="h-4 w-4" />}
                  onClick={() => {
                    showConfirm(
                      "Send Gold",
                      "Are you sure you want to send gold? This is normally only for special events. The code will stop you from doing this more than once.",
                      async () => {
                        await api.get<"/api/compo/:id/finalize-gold">(
                          `/api/compo/${compo_id}/finalize-gold`
                        );

                        updateCompo({ sent_gold: true });
                        alert("Done!");
                      },
                      "primary"
                    );
                  }}
                  grow={false}
                >
                  Send Gold
                </Button>
              </div>
            </div>
          </div>
        </Card>

        {/* Compo Info Form */}
        <Card>
          <CardTitle icon={<Cog6ToothIcon className="h-5 w-5" />}>
            Compo Settings
          </CardTitle>
          <form
            action={`/api/compo/${compo_id}/admin/save`}
            method="post"
            className="space-y-4"
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField label="Compo name">
                <input
                  type="text"
                  name="name"
                  defaultValue={compo.name}
                  className={inputClassName}
                />
              </FormField>
              <FormField label="Theme name">
                <input
                  type="text"
                  name="theme_short"
                  defaultValue={compo.theme_short}
                  className={inputClassName}
                />
              </FormField>
            </div>

            <FormField label="Top description">
              <textarea
                name="description"
                defaultValue={compo.description}
                className={textareaClassName}
              />
            </FormField>

            <div className="grid gap-4 sm:grid-cols-3">
              <FormField label="Start time">
                <DateTimeInput
                  name="start"
                  value={compo.startDate.toISOString()}
                  inputClassName={inputClassName}
                  dataTestId={TestIds.DateInputStart}
                />
              </FormField>
              <FormField label="Judging Start / Compo End">
                <DateTimeInput
                  name="stop"
                  value={compo.stopDate.toISOString()}
                  inputClassName={inputClassName}
                  dataTestId={TestIds.DateInputStop}
                />
              </FormField>
              <FormField label="Judging End">
                <DateTimeInput
                  name="judging_stop"
                  value={compo.judgingStopDate.toISOString()}
                  inputClassName={inputClassName}
                  dataTestId={TestIds.DateInputJudgingStop}
                />
              </FormField>
            </div>

            <FormField label="Theme description">
              <textarea
                name="theme"
                defaultValue={compo.theme}
                className={textareaClassName}
              />
            </FormField>

            <div className="grid gap-4 sm:grid-cols-2">
              <FormField label="Compo type">
                <input
                  type="text"
                  name="type"
                  placeholder="main, day, hour or meme"
                  defaultValue={compo.type}
                  className={inputClassName}
                />
              </FormField>
              <FormField label="Acceptable Entry Types">
                <input
                  type="text"
                  name="types"
                  placeholder="song, story, art"
                  defaultValue={compo.types.join(", ")}
                  className={inputClassName}
                />
              </FormField>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <FormField label="Primary Image">
                <input
                  type="text"
                  name="primary_image"
                  defaultValue={compo.original_primary_image}
                  className={inputClassName}
                />
              </FormField>
              <FormField label="Header Image">
                <input
                  type="text"
                  name="header_image"
                  defaultValue={compo.original_header_image}
                  className={inputClassName}
                />
              </FormField>
            </div>

            <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-600 dark:bg-slate-700/30">
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  name="nogold"
                  defaultChecked={compo.nogold}
                  className={checkboxClassName}
                />
                <span className="text-sm text-slate-700 dark:text-slate-200">
                  Make all entries to this compo free
                </span>
              </label>
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  name="allow_voting"
                  defaultChecked={compo.allow_voting}
                  className={checkboxClassName}
                />
                <span className="text-sm text-slate-700 dark:text-slate-200">
                  Allow voting on themes
                </span>
              </label>
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  name="hide_images_before_compo_start"
                  defaultChecked={compo.hide_images_before_compo_start}
                  className={checkboxClassName}
                  data-testid={TestIds.HideImagesBeforeCompoStartCheckbox}
                />
                <span className="text-sm text-slate-700 dark:text-slate-200">
                  Hide images before compo starts
                </span>
              </label>
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  name="is_secret_santa"
                  defaultChecked={compo.is_secret_santa}
                  className={`${checkboxClassName}`}
                />
                <div className="flex flex-col gap-1">
                  <span className="text-sm text-slate-700 dark:text-slate-200">
                    Secret Santa mode
                  </span>
                  <ul className="list-disc list-inside text-slate-500 dark:text-slate-400 text-sm">
                    <li>
                      Submissions will have an extra field for "who is this a
                      gift for?" and "what was your prompt?"
                    </li>
                  </ul>
                </div>
              </label>
            </div>

            <button
              type="submit"
              className="rounded-lg bg-blue-600 px-6 py-2.5 font-medium text-white transition hover:bg-blue-700"
              data-testid={TestIds.CompoSettingsSaveButton}
            >
              Save Changes
            </button>
          </form>
        </Card>

        {/* Give XP */}
        <Card>
          <CardTitle icon={<SparklesIcon className="h-5 w-5" />}>
            Give XP
          </CardTitle>
          <form action="/api/admin/givexp/" method="post" className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField label="Username">
                <input
                  type="text"
                  name="username"
                  defaultValue="johnfn"
                  className={inputClassName}
                />
              </FormField>
              <FormField label="Type">
                <input
                  type="text"
                  name="type"
                  defaultValue="Create Song"
                  placeholder="Create Song, Song Past Judgement"
                  className={inputClassName}
                />
              </FormField>
            </div>
            <button
              type="submit"
              className="rounded-lg bg-emerald-600 px-6 py-2.5 font-medium text-white transition hover:bg-emerald-700"
            >
              Give XP
            </button>
          </form>
        </Card>

        {/* Give Silver */}
        <Card>
          <CardTitle icon={<GiftIcon className="h-5 w-5" />}>
            Give Silver
          </CardTitle>
          <form
            action="/api/admin/givegold/"
            method="post"
            className="space-y-4"
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField label="Username">
                <input
                  type="text"
                  name="username"
                  defaultValue="johnfn"
                  className={inputClassName}
                />
              </FormField>
              <FormField label="Amount of Silver (20 Silver = 1 Gold)">
                <input
                  type="text"
                  name="silver"
                  defaultValue="10"
                  className={inputClassName}
                />
              </FormField>
            </div>
            <button
              type="submit"
              className="rounded-lg bg-amber-600 px-6 py-2.5 font-medium text-white transition hover:bg-amber-700"
            >
              Give Silver
            </button>
          </form>
        </Card>

        {/* Give Cacao */}
        <Card>
          <CardTitle icon={<CurrencyDollarIcon className="h-5 w-5" />}>
            Give Cacao Points
          </CardTitle>
          <form
            action="/api/admin/givecacao/"
            method="post"
            className="space-y-4"
          >
            <div className="grid gap-4 sm:grid-cols-3">
              <FormField label="Username">
                <input
                  type="text"
                  name="username"
                  defaultValue="johnfn"
                  className={inputClassName}
                />
              </FormField>
              <FormField label="Amount">
                <input
                  type="text"
                  name="cacao"
                  defaultValue="10"
                  className={inputClassName}
                />
              </FormField>
              <FormField label="Reason">
                <input
                  type="text"
                  name="reason"
                  defaultValue="Round 1 Placement"
                  className={inputClassName}
                />
              </FormField>
            </div>
            <button
              type="submit"
              className="rounded-lg bg-orange-600 px-6 py-2.5 font-medium text-white transition hover:bg-orange-700"
            >
              Give Cacao
            </button>
          </form>
        </Card>

        {/* Category Names */}
        <Card>
          <CardTitle icon={<TagIcon className="h-5 w-5" />}>
            Category Names
          </CardTitle>
          <form
            action={`/api/compo/${compo_id}/admin/save-categories`}
            method="post"
            className="space-y-4"
          >
            <Selector prefix="entry type" choices={["song", "story", "art"]}>
              <div className="space-y-3">
                <input type="hidden" name="type" value="song" />
                <p className="text-sm font-medium text-slate-600 dark:text-slate-300">
                  Song Categories
                </p>
                <div className="grid gap-2 sm:grid-cols-3">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
                    <input
                      key={`song${i}`}
                      type="text"
                      name={`category_${i}`}
                      placeholder={`Category ${i}`}
                      defaultValue={
                        categories
                          ? new CompoCategoriesClient(
                              categories.find((x) => x.type === "song")
                            ).list[i - 1]
                          : ""
                      }
                      className={inputClassName}
                    />
                  ))}
                </div>
              </div>
              <div className="space-y-3">
                <input type="hidden" name="type" value="story" />
                <p className="text-sm font-medium text-slate-600 dark:text-slate-300">
                  Story Categories
                </p>
                <div className="grid gap-2 sm:grid-cols-3">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
                    <input
                      key={`story${i}`}
                      type="text"
                      name={`category_${i}`}
                      placeholder={`Category ${i}`}
                      defaultValue={
                        categories
                          ? new CompoCategoriesClient(
                              categories.find((x) => x.type === "story")
                            ).list[i - 1]
                          : ""
                      }
                      className={inputClassName}
                    />
                  ))}
                </div>
              </div>
              <div className="space-y-3">
                <input type="hidden" name="type" value="art" />
                <p className="text-sm font-medium text-slate-600 dark:text-slate-300">
                  Art Categories
                </p>
                <div className="grid gap-2 sm:grid-cols-3">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
                    <input
                      key={`art${i}`}
                      type="text"
                      name={`category_${i}`}
                      placeholder={`Category ${i}`}
                      defaultValue={
                        categories
                          ? new CompoCategoriesClient(
                              categories.find((x) => x.type === "art")
                            ).list[i - 1]
                          : ""
                      }
                      className={inputClassName}
                    />
                  ))}
                </div>
              </div>
            </Selector>
            <button
              type="submit"
              className="rounded-lg bg-violet-600 px-6 py-2.5 font-medium text-white transition hover:bg-violet-700"
            >
              Save Categories
            </button>
          </form>
        </Card>
      </div>

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal((prev) => ({ ...prev, isOpen: false }))}
        onConfirm={() => {
          confirmModal.onConfirm();
          setConfirmModal((prev) => ({ ...prev, isOpen: false }));
        }}
        title={confirmModal.title}
        confirmVariant={confirmModal.variant}
      >
        {confirmModal.message}
      </ConfirmModal>

      <ConfirmModal
        isOpen={votesAlreadyExistModal.isOpen}
        onClose={() =>
          setVotesAlreadyExistModal((prev) => ({ ...prev, isOpen: false }))
        }
        onConfirm={() => {
          handleGenerateVotes(compo_id);
          setVotesAlreadyExistModal((prev) => ({ ...prev, isOpen: false }));
        }}
        title="Required votes have already been generated"
        confirmVariant="danger"
      >
        <p>
          {votesAlreadyExistModal.uniqueVoters} people have made{" "}
          {votesAlreadyExistModal.totalVotes} total votes - are you sure you
          want to regenerate required votes?
        </p>
      </ConfirmModal>

      <ConfirmModal
        isOpen={finalizeModalOpen}
        onClose={() => setFinalizeModalOpen(false)}
        onConfirm={() => {
          setFinalizeModalOpen(false);
          handleCommitVotingResults();
        }}
        title="Finalize Compo"
        confirmVariant="danger"
        confirmDisabled={!finalizeConfirmed}
      >
        <div className="space-y-4">
          <div>
            <p className="font-medium text-slate-700 dark:text-slate-200">
              This will:
            </p>
            <ul className="mt-1 list-disc list-inside text-sm text-slate-600 dark:text-slate-400">
              <li>Deanonymize all entries</li>
              <li>Make all scores public</li>
              <li>Allow everyone to see the results page</li>
            </ul>
          </div>
          <div>
            <p className="font-medium text-slate-700 dark:text-slate-200">
              Finalization checklist:
            </p>
            <ul className="mt-1 space-y-1 text-sm">
              <li>
                <div className="flex items-center gap-2">
                  {(votesPerEntry || 0) >= 8 ? (
                    <CheckIcon className="h-4 w-4 text-emerald-500" />
                  ) : (
                    <XMarkIcon className="h-4 w-4 text-red-500" />
                  )}
                  <span className="text-slate-600 dark:text-slate-400">
                    Average votes per entry: {votesPerEntry || 0}
                  </span>
                </div>
                {(votesPerEntry || 0) < 8 && (
                  <p className="ml-6 text-amber-600 dark:text-amber-400">
                    Consider extending the deadline to get more voters.
                  </p>
                )}
              </li>
              <li className="flex items-center gap-2">
                {new Date() > compo.judgingStopDate ? (
                  <CheckIcon className="h-4 w-4 text-emerald-500" />
                ) : (
                  <XMarkIcon className="h-4 w-4 text-red-500" />
                )}
                <span className="text-slate-600 dark:text-slate-400">
                  {new Date() > compo.judgingStopDate
                    ? "Judging deadline has passed"
                    : "Judging deadline has not passed yet"}
                </span>
              </li>
            </ul>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={finalizeConfirmed}
              onChange={(e) => setFinalizeConfirmed(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-700"
            />
            <span className="text-sm text-slate-600 dark:text-slate-400">
              Yes, everything looks good
            </span>
          </label>
        </div>
      </ConfirmModal>
    </div>
  );
}
